'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  useReactFlow,
  useNodesInitialized,
  type Node,
  type Edge,
} from '@xyflow/react';
import {
  getGraphCollectionFingerprint,
  updateGraphFingerprintHash,
} from './graph-fingerprint';
import { getHierarchyLayout } from './hierarchy-layout';
import { getRadialLayout } from './radial-layout';
import { getForceAtlas2Layout } from './force-atlas2-layout';
import { getTimeAxisLayout } from './time-axis-layout';
import type {
  EntityKindAwareNodeData,
  GraphLayoutAlgorithm,
  HierarchyRankDirection,
  NodePosition,
} from './layout-types';

export type { EntityKindAwareNodeData, HierarchyRankDirection };

export type AutoLayoutConfig<
  TNodeData extends EntityKindAwareNodeData = EntityKindAwareNodeData,
> = {
  layout?: GraphLayoutAlgorithm;
  nodesep?: number;
  ranksep?: number;
  rankdir?: HierarchyRankDirection;
  fitViewPadding?: number;
  fitViewMaxZoom?: number;
  layoutDependency?: string | number;
  preserveViewportOnGrowth?: boolean;
  waitForNodeMeasurement?: boolean;
  sourceNodes?: Node<TNodeData>[];
  sourceEdges?: Edge[];
  onLayoutApplied?: () => void;
};

const GROWTH_OVERLAP_THRESHOLD = 0.5;
const MAX_LAYOUT_CACHE_ENTRIES = 5;
const FIT_VIEW_REVEAL_TIMEOUT_MS = 1500;

type LayoutCache = Map<string, Map<string, NodePosition>>;

function isPureNodeGrowth({
  previousNodeIds,
  currentNodeIds,
}: {
  previousNodeIds: Set<string> | null;
  currentNodeIds: Set<string>;
}): boolean {
  if (!previousNodeIds || previousNodeIds.size === 0) {
    return false;
  }

  if (currentNodeIds.size <= previousNodeIds.size) {
    return false;
  }

  const overlapCount = [...previousNodeIds].filter((id) =>
    currentNodeIds.has(id),
  ).length;

  return overlapCount >= previousNodeIds.size * GROWTH_OVERLAP_THRESHOLD;
}

function getCachedLayout({
  cache,
  signature,
}: {
  cache: LayoutCache;
  signature: string;
}) {
  const cachedLayout = cache.get(signature);

  if (!cachedLayout) return null;
  cache.delete(signature);
  cache.set(signature, cachedLayout);

  return cachedLayout;
}

function setCachedLayout({
  cache,
  signature,
  layout,
}: {
  cache: LayoutCache;
  signature: string;
  layout: Map<string, NodePosition>;
}) {
  cache.set(signature, layout);
  while (cache.size > MAX_LAYOUT_CACHE_ENTRIES) {
    const oldestSignature = cache.keys().next().value;

    if (oldestSignature === undefined) return;
    cache.delete(oldestSignature);
  }
}

function getLayoutSignature({
  nodes,
  edges,
  layout,
  nodesep,
  ranksep,
  rankdir,
}: {
  nodes: Node[];
  edges: Edge[];
  layout: GraphLayoutAlgorithm;
  nodesep: number;
  ranksep: number;
  rankdir: HierarchyRankDirection;
}) {
  const nodeFingerprint = getGraphCollectionFingerprint({
    items: nodes,
    hashItem: (seed, node) => updateGraphFingerprintHash(seed, node.id),
  });
  const edgeFingerprint = getGraphCollectionFingerprint({
    items: edges,
    hashItem: (seed, edge) => {
      const sourceHash = updateGraphFingerprintHash(seed, edge.source);
      const separatorHash = updateGraphFingerprintHash(sourceHash, '\0');

      return updateGraphFingerprintHash(separatorHash, edge.target);
    },
  });

  return [
    nodeFingerprint,
    edgeFingerprint,
    layout,
    nodesep,
    ranksep,
    rankdir,
  ].join('|');
}

function computeLayout<TNodeData extends EntityKindAwareNodeData>({
  layout,
  nodes,
  edges,
  nodesep,
  ranksep,
  rankdir,
}: {
  layout: GraphLayoutAlgorithm;
  nodes: Node<TNodeData>[];
  edges: Edge[];
  nodesep: number;
  ranksep: number;
  rankdir: HierarchyRankDirection;
}): Map<string, NodePosition> {
  if (layout === 'hierarchy') {
    return getHierarchyLayout({ nodes, edges, nodesep, ranksep, rankdir });
  }

  if (layout === 'forceAtlas2') {
    return getForceAtlas2Layout({ nodes, edges, nodeSpacing: nodesep });
  }

  if (layout === 'timeAxis') {
    return getTimeAxisLayout({ nodes, edges, nodesep, ranksep });
  }

  return getRadialLayout({ nodes, edges });
}

export type AutoLayoutState = {
  isLayoutPending: boolean;
};

export function useAutoLayout<
  TNodeData extends EntityKindAwareNodeData = EntityKindAwareNodeData,
>(config?: AutoLayoutConfig<TNodeData>): AutoLayoutState {
  const {
    layout = 'radial',
    nodesep = 220,
    ranksep = 600,
    rankdir = 'LR',
    fitViewPadding = 0.1,
    fitViewMaxZoom,
    layoutDependency,
    preserveViewportOnGrowth = false,
    waitForNodeMeasurement = true,
    sourceNodes,
    sourceEdges,
    onLayoutApplied,
  } = config ?? {};
  const { setNodes, getNodes, getEdges, fitView } =
    useReactFlow<Node<TNodeData>>();
  const nodesInitialized = useNodesInitialized();
  const layoutCache = useRef<LayoutCache>(new Map());
  const lastLayoutSignature = useRef<string | null>(null);
  const previousNodeIdsRef = useRef<Set<string> | null>(null);
  const layoutRequestId = useRef(0);
  const workerRef = useRef<Worker | null>(null);
  const onLayoutAppliedRef = useRef(onLayoutApplied);
  const fitViewRequestId = useRef(0);
  const [isLayoutPending, setIsLayoutPending] = useState(true);

  onLayoutAppliedRef.current = onLayoutApplied;

  const runLayout = useCallback(() => {
    const nodes = sourceNodes ?? getNodes();
    const edges = sourceEdges ?? getEdges();

    if (nodes.length === 0) {
      setIsLayoutPending(false);

      return;
    }

    const currentNodeIds = new Set(nodes.map((node) => node.id));
    const shouldSkipFitView =
      preserveViewportOnGrowth &&
      isPureNodeGrowth({
        previousNodeIds: previousNodeIdsRef.current,
        currentNodeIds,
      });

    previousNodeIdsRef.current = currentNodeIds;

    const requestedFitViewId = fitViewRequestId.current + 1;

    fitViewRequestId.current = requestedFitViewId;

    if (!shouldSkipFitView) {
      setIsLayoutPending(true);
    }

    const maybeFitView = () => {
      if (shouldSkipFitView) {
        setIsLayoutPending(false);

        return;
      }

      const revealLayout = () => {
        if (fitViewRequestId.current === requestedFitViewId) {
          setIsLayoutPending(false);
        }
      };

      setTimeout(() => {
        if (fitViewRequestId.current !== requestedFitViewId) return;
        setTimeout(revealLayout, FIT_VIEW_REVEAL_TIMEOUT_MS);
        fitView({
          duration: 0,
          padding: fitViewPadding,
          maxZoom: fitViewMaxZoom,
        })
          .catch((error: unknown) => {
            console.warn(
              'Graph fitView failed; revealing unfitted layout',
              error,
            );
          })
          .finally(revealLayout);
      }, 50);
    };

    const signature = getLayoutSignature({
      nodes,
      edges,
      layout,
      nodesep,
      ranksep,
      rankdir,
    });

    const cachedLayout = getCachedLayout({
      cache: layoutCache.current,
      signature,
    });

    if (cachedLayout) {
      setNodes(
        nodes.map((node) => {
          const position = cachedLayout.get(node.id);

          return position ? { ...node, position } : node;
        }),
      );
      maybeFitView();
      onLayoutAppliedRef.current?.();

      return;
    }

    const applyPositions = (computedLayout: Map<string, NodePosition>) => {
      setCachedLayout({
        cache: layoutCache.current,
        signature,
        layout: computedLayout,
      });
      setNodes(
        nodes.map((node) => {
          const position = computedLayout.get(node.id);

          return position ? { ...node, position } : node;
        }),
      );
      maybeFitView();
      onLayoutAppliedRef.current?.();
    };

    if (layout === 'forceAtlas2' && typeof Worker !== 'undefined') {
      const requestId = layoutRequestId.current + 1;

      layoutRequestId.current = requestId;

      try {
        const worker =
          workerRef.current ??
          new Worker(new URL('./force-atlas2.worker.ts', import.meta.url));

        workerRef.current = worker;

        const handleMessage = (
          event: MessageEvent<{
            requestId: number;
            positions: Array<[string, NodePosition]>;
          }>,
        ) => {
          if (event.data.requestId !== requestId) {
            return;
          }

          worker.removeEventListener('message', handleMessage);
          worker.removeEventListener('error', handleError);
          applyPositions(new Map(event.data.positions));
        };

        const handleError = () => {
          worker.removeEventListener('message', handleMessage);
          worker.removeEventListener('error', handleError);

          if (layoutRequestId.current !== requestId) {
            return;
          }

          applyPositions(
            computeLayout({
              layout,
              nodes,
              edges,
              nodesep,
              ranksep,
              rankdir,
            }),
          );
        };

        worker.addEventListener('message', handleMessage);
        worker.addEventListener('error', handleError);
        worker.postMessage({
          requestId,
          nodes,
          edges,
          nodeSpacing: nodesep,
        });

        return;
      } catch (error: unknown) {
        workerRef.current = null;
        console.warn(
          'ForceAtlas2 worker unavailable; laying out on the main thread',
          error,
        );
      }
    }

    applyPositions(
      computeLayout({
        layout,
        nodes,
        edges,
        nodesep,
        ranksep,
        rankdir,
      }),
    );
  }, [
    getNodes,
    getEdges,
    setNodes,
    fitView,
    nodesep,
    ranksep,
    fitViewPadding,
    fitViewMaxZoom,
    layout,
    rankdir,
    preserveViewportOnGrowth,
    sourceNodes,
    sourceEdges,
  ]);

  useEffect(() => {
    lastLayoutSignature.current = null;
  }, [layoutDependency]);

  useEffect(() => {
    return () => {
      workerRef.current?.terminate();
      workerRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (waitForNodeMeasurement && !nodesInitialized) return;
    const nodes = sourceNodes ?? getNodes();
    const edges = sourceEdges ?? getEdges();
    const signature = getLayoutSignature({
      nodes,
      edges,
      layout,
      nodesep,
      ranksep,
      rankdir,
    });

    if (lastLayoutSignature.current === signature) {
      return;
    }

    lastLayoutSignature.current = signature;
    runLayout();
  }, [
    nodesInitialized,
    sourceNodes,
    sourceEdges,
    waitForNodeMeasurement,
    getEdges,
    getNodes,
    layout,
    nodesep,
    ranksep,
    rankdir,
    runLayout,
    layoutDependency,
  ]);

  return { isLayoutPending };
}

export {
  updateGraphFingerprintHash,
  getGraphCollectionFingerprint,
  shouldPreserveNodePositions,
} from './graph-fingerprint';
